import { useEffect } from "react";
import useSWR from "swr";

import { receiveAllLADSAutomations } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import { useAutomationsStore } from "../store/automations-store";
import type { ViewAutomation, WeekDays } from "../view-model";
import { useActiveRoom } from "./use-rooms";

function convertTimeToHHMM(time: string | null): string {
  if (!time) return "00:00";
  return time.slice(0, 5);
}

function mapDayNumberToWeekDay(dayNumber: number): WeekDays {
  const dayMap: Record<number, WeekDays> = {
    1: "day1",
    2: "day2",
    3: "day3",
    4: "day4",
    5: "day5",
    6: "day6",
    7: "day7",
  };
  return dayMap[dayNumber] || "day1";
}

const receiveAutomations = async (
  roomId?: string,
  accessToken?: string
): Promise<ViewAutomation[]> => {
  const response = await receiveAllLADSAutomations(
    roomId,
    undefined,
    accessToken
  );
  const automations = response.data;

  const sortedAutomations = automations?.sort((automation1, automation2) => {
    const date1 = new Date(automation1.createdAt || "");
    const date2 = new Date(automation2.createdAt || "");
    return date2.getTime() - date1.getTime();
  });

  const groupedAutomations = new Map<
    string,
    { light?: ViewAutomation["light"]; operation?: ViewAutomation["operation"] }
  >();

  for (const automation of sortedAutomations || []) {
    const pathLower = automation.targetFunction.path.toLowerCase();
    const mode = pathLower.includes("light") ? "light" : "operation";

    const key = automation.deviceId;

    if (!groupedAutomations.has(key)) {
      groupedAutomations.set(key, {});
    }

    const group = groupedAutomations.get(key);
    if (!group) continue;

    if (!group[mode]) {
      group[mode] = {
        id: automation.id,
        deviceId: automation.deviceId,
        enabled: automation.enabled,
        name: automation.name,
      };
    }

    const scheduleStart = convertTimeToHHMM(automation.schedule.start);
    const scheduleEnd = convertTimeToHHMM(automation.schedule.end);

    const targetValueStart =
      automation.targetFunction.targetValueStart?.toLowerCase();
    const isOnCommand =
      targetValueStart === "on" || targetValueStart === "normal";
    const isOffCommand =
      targetValueStart === "off" || targetValueStart === "low";

    const period = automation.schedule.period;
    let periodArray: number[] = [];
    if (Array.isArray(period)) {
      periodArray = period;
    } else if (period) {
      periodArray = [...period];
    }

    if (periodArray.length > 0) {
      for (const dayNumber of periodArray) {
        if (typeof dayNumber !== "number") continue;
        const weekDay = mapDayNumberToWeekDay(dayNumber);
        const automationMode = group[mode];
        if (automationMode) {
          if (automationMode[weekDay]) {
            const existing = automationMode[weekDay];
            if (existing) {
              if (isOnCommand && scheduleStart && scheduleStart !== "00:00") {
                existing.startTime = scheduleStart;
              }
              if (isOffCommand && scheduleStart && scheduleStart !== "00:00") {
                existing.endTime = scheduleStart;
              }
              if (scheduleEnd && scheduleEnd !== "00:00") {
                existing.endTime = scheduleEnd;
              }
            }
          } else {
            automationMode[weekDay] = {
              startTime: isOffCommand ? "00:00" : scheduleStart,
              endTime:
                scheduleEnd && scheduleEnd !== "00:00"
                  ? scheduleEnd
                  : scheduleStart,
            };
          }
        }
      }
    }
  }

  const viewAutomations: ViewAutomation[] = [
    ...groupedAutomations.values(),
  ].map((group) => {
    const result: ViewAutomation = {};
    if (group.light) result.light = group.light;
    if (group.operation) result.operation = group.operation;
    return result;
  });

  return viewAutomations;
};

export function useAutomations() {
  const {
    automations,
    error,
    isLoading,
    setAutomations,
    setError,
    setLoading,
  } = useAutomationsStore();
  const { activeRoom } = useActiveRoom();
  const accessToken = useAccessToken();

  const roomId = activeRoom?.id;

  // Use SWR for initial data fetching and caching
  const {
    data,
    error: swrError,
    mutate,
  } = useSWR(
    ["receiveAllLADSAutomations", roomId, accessToken || undefined],
    ([, room, token]) => receiveAutomations(room, token),
    {
      refreshInterval: 5000, // 5 second polling interval
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Sync SWR data to Zustand
  useEffect(() => {
    if (data) {
      setAutomations(data);
      setLoading(false);
      setError(null);
    }
  }, [data, setAutomations, setLoading, setError]);

  // Handle SWR errors
  useEffect(() => {
    if (swrError) {
      setError(swrError.message);
      setLoading(false);
    }
  }, [swrError, setError, setLoading]);

  return {
    automations,
    error,
    isLoading,
    mutate,
  };
}
